import { store, Unsubscribe } from "@stencil/redux";
import {
  ConversationFactory,
  IMessage,
  IMember,
  ConversationTypeEnum,
  IQuickReply,
  ISessionResponseStructuredMessageBodyContentButtonResponse,
  CobrowseRequestType,
} from "@auvious/integrations";
import {
  Component,
  ComponentInterface,
  h,
  Prop,
  Event,
  EventEmitter,
  State,
  Watch,
} from "@stencil/core";

import {
  TranslateService,
  ThemeService,
  DeviceService,
  Log,
  AppConfigService as Config,
  CommonService,
  CobrowseService,
  AppConfigService,
  StorageService,
} from "../../services";
import {
  IThemeOptions,
  ConversationStateEnum,
  IEndpointMetadata,
} from "../../models";
import { RootState } from "../../store/states";
import { NotificationService } from "../../services/notification.service";
import {
  CallStateEnum,
  CobrowsePermissionEnum,
  CobrowseStateEnum,
  DisplayCaptureDirectionEnum,
  DisplayCaptureStateEnum,
  ErrorCode,
} from "../../models/enums";
import { HandledError } from "../../models/HandledError";
import { AVBusService } from "../../services/bus.service";
import { ErrorHandler } from "../../services/error.handler.service";
import {
  dispatchConversationMinimizeChange,
  dispatchMessageMetadataChange,
} from "../../store/actions/conversation";
import moment from "moment";
import { marked, RendererObject, TokenizerObject } from "marked";
import DOMPurify from "isomorphic-dompurify";

const KEY_METADATA_REPLY_TO_MESSAGE_ID =
  "auvious.origin.widget.message.reply.id";
@Component({
  tag: "app-conversation",
  styleUrl: "app-conversation.scss",
  shadow: true,
  assetsDirs: ["assets"],
})
export class AppConversation implements ComponentInterface {
  @Prop() focused: boolean;

  @Prop({ mutable: true }) $isAgentTyping: boolean;
  @Prop({ mutable: true }) $messages: IMessage[];
  @Prop({ mutable: true }) $agent: IMember;
  @Prop({ mutable: true }) $conversationType: ConversationTypeEnum;
  @Prop({ mutable: true }) $conversationState: ConversationStateEnum;
  @Prop({ mutable: true }) $isPoppedOut: boolean;
  @Prop({ mutable: true }) $isMinimized: boolean;
  @Prop({ mutable: true }) $cobrowseState: CobrowseStateEnum;
  @Prop({ mutable: true }) $cobrowseRequestType: CobrowseRequestType;
  @Prop({ mutable: true }) $cobrowsePermissions: {
    [key in CobrowsePermissionEnum]: boolean;
  };
  @Prop({ mutable: true }) $callState: CallStateEnum;
  @Prop({ mutable: true }) $ticket: string;
  @Prop({ mutable: true }) $displayCaptureState: DisplayCaptureStateEnum;
  @Prop({ mutable: true })
  $displayCaptureDirection: DisplayCaptureDirectionEnum;
  @Prop({ mutable: true }) $cobrowseRemoteEndpointMeta: IEndpointMetadata;

  @Event({ bubbles: false }) close: EventEmitter;

  @State() themeOptions: IThemeOptions;
  @State() attachments: File[] = [];
  @State() pendingDownloads: { [key: string]: boolean } = {};
  @State() unreadMessagesCount = 0;
  @State() open: boolean;

  @Watch("$messages")
  @Watch("$isAgentTyping")
  messageReceived() {
    this.scrollToBottom();
  }

  @Watch("$messages")
  messageReceivedUnread(messages: IMessage[]) {
    if (this.$isMinimized && messages.length > 0) {
      const latestMessage = messages[messages.length - 1];
      if (!latestMessage.isLocal && latestMessage.status === "ack") {
        ++this.unreadMessagesCount;
      }
    }
  }

  @Watch("focused")
  focusChange(isFocused) {
    if (isFocused) this.inputRef.focus();
  }

  dispatchConversationMinimizeChange: typeof dispatchConversationMinimizeChange;
  dispatchMessageMetadataChange: typeof dispatchMessageMetadataChange;

  message: string;
  inputRef: HTMLInputElement;
  bodyRef: HTMLDivElement;
  fileInputRef: HTMLInputElement;
  storeUnsubscribe: Unsubscribe;
  // acceptedAttachmentFileTypes: string;
  // acceptedAttachmentFileSize: number;
  attachmentConstraints: {
    fileSize: number;
    mimeType: string;
    friendlyFileSize: string;
  };

  constructor() {
    ThemeService.optionsAvailable().subscribe(
      (options) => (this.themeOptions = options)
    );
  }

  connectedCallback() {
    if (this.storeUnsubscribe) this.storeUnsubscribe();

    this.storeUnsubscribe = store.mapStateToProps(
      this,
      (rootState: RootState) => {
        const {
          conversation: {
            state,
            messages,
            agent,
            isAgentTyping,
            type,
            isChatMinimized,
          },
          call: { isPopedOut, ticket },
          cobrowse: { permissions, requestType, remoteEndpointMetadata },
          displayCapture: { direction },
        } = rootState;

        const cobrowseState = rootState.cobrowse.state;
        const callState = rootState.call.state;
        const displayCaptureState = rootState.displayCapture.state;

        return {
          $conversationState: state,
          $messages: messages,
          $agent: agent,
          $isAgentTyping: isAgentTyping,
          $conversationType: type,
          $isPoppedOut: isPopedOut,
          $isMinimized: isChatMinimized,
          $cobrowseState: cobrowseState,
          $cobrowsePermissions: permissions,
          $callState: callState,
          $ticket: ticket,
          $displayCaptureDirection: direction,
          $displayCaptureState: displayCaptureState,
          $cobrowseRequestType: requestType,
          $cobrowseRemoteEndpointMeta: remoteEndpointMetadata,
        };
      }
    );

    store.mapDispatchToProps(this, {
      dispatchConversationMinimizeChange,
      dispatchMessageMetadataChange,
    });
  }

  componentWillLoad(): Promise<void> | void {
    if (Config.chatMode === "genesys-web-messaging") {
      this.attachmentConstraints = {
        mimeType:
          Config.webMessagingAttachmentFileTypes ??
          ConversationFactory.instance.attachmentConstraints.mimeType,
        fileSize:
          Config.webMessagingAttachmentFileSize ??
          ConversationFactory.instance.attachmentConstraints.fileSize,
        friendlyFileSize:
          Config.webMessagingAttachmentFileSizeUnit ??
          ConversationFactory.instance.attachmentConstraints.friendlyFileSize,
      };
    } else {
      this.attachmentConstraints =
        ConversationFactory.instance.attachmentConstraints;
    }

    this.initMarkdown();
  }

  componentDidLoad(): void {
    this.open = true;
    this.scrollToBottom();
    CobrowseService.listenDisplayCaptureEndedByUser(() => {
      this.onDisplayCaptureStopped();
    });
    this.focusInputField();
  }

  disconnectedCallback() {
    this.unreadMessagesCount = 0;
    this.open = false;
    this.storeUnsubscribe();
  }

  /**
   * in markdown *text* and _text_ are both <em> (italic)
   * in genesys cloud *text* is bold and _text_ is italic.
   * so we need a custom tokenizer for em / strong
   */
  initMarkdown() {
    const tokenizer: TokenizerObject = {
      emStrong(src) {
        const match = src.match(/^(\*\*?|__?)([^*_]+)\1/);
        if (match) {
          const text = match[2];
          const tokenLex = marked.lexer(text);
          return {
            type: match[1] === "*" ? "strong" : "em", // *text* -> strong, _text_ -> em
            raw: match[0],
            text,
            // @ts-expect-error
            tokens: tokenLex[0].tokens as Tokens[], // Ensure text is properly tokenized
          };
        }
      },
      codespan(src) {
        const match = src.match(/^`([^`]+)`/);
        if (match) {
          return {
            type: "codespan",
            raw: match[0],
            text: match[1],
          };
        }
      },
      code(src) {
        const match = src.match(/^```([\s\S]+?)```/);
        if (match) {
          return {
            type: "code",
            raw: match[0],
            text: match[1].trim(),
            lang: null, // Set to null unless language is explicitly provided
          };
        }
      },
    };

    const renderer: RendererObject = {
      link(tokens) {
        return `<a href="${tokens.href}" target="_blank" class="av-chat-message-link" rel="noopener noreferrer">${tokens.text}</a>`;
      },
    };

    function postprocess(html) {
      return DOMPurify.sanitize(html);
    }

    marked.use({ renderer, tokenizer, hooks: { postprocess } });
  }

  async send(event) {
    event.preventDefault();
    if (!this.message) return;
    const message = this.message?.trim?.();
    // send
    try {
      // reset
      event.target[0].value = null;
      this.message = null;

      // try to find if we have a pending bot question. If we do, we need to remove the available bot buttons
      let metadata;
      if (
        ["genesys-cloud", "genesys-premise", "genesys-web-messaging"].includes(
          Config.chatMode
        ) &&
        this.$messages.length > 0
      ) {
        const lastMessage = this.$messages[this.$messages.length - 1];
        if (
          lastMessage.type === "bot" &&
          lastMessage.quickReplies?.length > 0
        ) {
          metadata = { [KEY_METADATA_REPLY_TO_MESSAGE_ID]: lastMessage.id };
        }
      }

      // send
      await ConversationFactory.instance.sendMessage(
        message,
        "standard",
        metadata
      );
    } catch (ex) {
      ErrorHandler.captureMessage(["conversation.on.send", ex]);
      ErrorHandler.log(
        new HandledError(ex, ErrorCode.CONVERSATION_MESSAGE_SEND_FAILED)
      );
    }
  }

  onClose() {
    this.close.emit();
  }

  onDisplayCaptureStopped() {
    if (this.$cobrowseRequestType === "display-capture") {
      CobrowseService.terminate("user-terminated");
    }
  }

  handleChange(event) {
    this.message = event.target.value;
  }

  registerBodyRef(ref) {
    if (!this.bodyRef) {
      this.bodyRef = ref;
      this.scrollToBottom();
    }
  }

  private focusInputField() {
    // requestAnimationFrame(() => setTimeout(() => this.inputRef?.focus(), 10));
    setTimeout(() => requestAnimationFrame(() => this.inputRef?.focus()), 10);
  }

  toggleMinimize() {
    if (this.$isMinimized) {
      this.scrollToBottom();
      this.focusInputField();
      this.unreadMessagesCount = 0;
    }
    this.dispatchConversationMinimizeChange(!this.$isMinimized);
  }

  transformToHTML(message: IMessage) {
    const bubbleStyle = {
      backgroundColor: this.themeOptions.colorAccent,
    };
    const style = message.isLocal ? bubbleStyle : {};
    const cssClass = {
      "av-chat-message": true,
      "av-chat-message-error": message.status == "error",
      "av-chat-message-pending":
        message.status == "sent" || this.pendingDownloads[message.file?.id],
    };
    switch (message.type) {
      case "text":
        // announce sent status to screen reader
        const renderText =
          message.status === "ack" && message.isLocal
            ? [
                <span
                  role="alert"
                  aria-label={TranslateService.instant("Message sent")}
                ></span>,
              ]
            : [];

        renderText.push(
          <div style={style} class={cssClass}>
            {this.transformTextMessageToHTML(message)}
          </div>
        );

        const hint = [];
        if (message.timestamp) hint.push(this.dateToHTML(message.timestamp));
        if (
          AppConfigService.chatNicknamesEnabled &&
          message.senderName &&
          !message.isLocal
        )
          hint.push(message.senderName);
        if (hint.length > 0) {
          renderText.push(
            <div class={{ "av-chat-message-hint": true }}>
              {hint.join(" • ")}
            </div>
          );
        }
        return renderText;
      case "notice":
        let text;
        switch (message.noticeType) {
          case "participant-joined":
            text = !!message.senderName
              ? [
                  message.senderName,
                  TranslateService.instant("connected"),
                ].join(" ")
              : TranslateService.instant("agent connected");
            break;
          case "participant-left":
            text = !!message.senderName
              ? [
                  message.senderName,
                  TranslateService.instant("disconnected"),
                ].join(" ")
              : TranslateService.instant("agent disconnected");
            break;
        }
        if (!text) return;

        const notices = [];
        if (message.timestamp) notices.push(this.dateToHTML(message.timestamp));
        // if (message.senderName) notices.push(message.senderName);

        const renderNotice = [
          <div class={{ "av-chat-notice": true }}>
            <div class={{ "av-chat-notice-message": true }}>{text}</div>
          </div>,
        ];
        if (notices.length > 0) {
          renderNotice.push(
            <div class={{ "av-chat-message-hint": true }}>
              {notices.join(" • ")}
            </div>
          );
        }
        return renderNotice;
      case "file":
        const renderFile = [
          <div style={style} class={cssClass}>
            {this.transformFileMessageToHTML(message)}
          </div>,
        ];
        const fileHint = [];
        if (message.timestamp)
          fileHint.push(this.dateToHTML(message.timestamp));
        if (
          AppConfigService.chatNicknamesEnabled &&
          message.senderName &&
          !message.isLocal
        )
          fileHint.push(message.senderName);
        if (fileHint.length > 0) {
          renderFile.push(
            <div class={{ "av-chat-message-hint": true }}>
              {fileHint.join(" • ")}
            </div>
          );
        }
        return renderFile;
      case "bot":
        return this.transformBotMessageToHTML(message);
    }
  }

  transformBotMessageToHTML(message: IMessage) {
    let hasReplied = this.$messages.some(
      (m) => m.metadata?.[KEY_METADATA_REPLY_TO_MESSAGE_ID] === message.id
    );

    if (!hasReplied && message.quickReplies?.length > 0) {
      // on page refresh we will not have the metadata, so only show the quick replies if it's the last message
      const lastMessage = this.$messages.sort((m1, m2) =>
        !!m1.index && !!m2.index
          ? m1.index - m2.index
          : m1.timestamp < m2.timestamp
          ? -1
          : 1
      )[this.$messages.length - 1];

      hasReplied = lastMessage.id !== message.id;
    }
    const hint = [];
    if (message.timestamp) hint.push(this.dateToHTML(message.timestamp));
    if (
      AppConfigService.chatNicknamesEnabled &&
      message.senderName &&
      !message.isLocal
    )
      hint.push(message.senderName);

    // if (fileHint.length > 0) {
    //   renderFile.push(
    //     <div class={{ "av-chat-message-hint": true }}>
    //       {hint.join(" • ")}
    //     </div>
    //   );
    // }

    return (
      <div class="av-chat-message-bot">
        <div>
          <div class="av-chat-message">{this.markdownToHTML(message.text)}</div>
          {hint.length > 0 && (
            <div class={{ "av-chat-message-hint": true }}>
              {hint.join(" • ")}
            </div>
          )}
        </div>
        {!hasReplied && message.quickReplies?.length > 0 && (
          <div class="av-chat-message-bot-replies">
            {message.quickReplies?.map((reply) => {
              return (
                <av-button
                  color="link"
                  shape="bordered"
                  size="md"
                  onClick={() => this.quickReply(message, reply)}
                >
                  {reply.text}
                </av-button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  quickReply(message: IMessage, reply: IQuickReply) {
    let metadata;
    let bodyType;
    switch (Config.chatMode) {
      case "genesys-web-messaging":
        bodyType = "content";
        const content: ISessionResponseStructuredMessageBodyContentButtonResponse[] =
          [
            {
              contentType: "ButtonResponse",
              buttonResponse: {
                payload: reply.payload,
                text: reply.text,
                type: "QuickReply",
              },
            },
          ];
        metadata = {
          content,
          [KEY_METADATA_REPLY_TO_MESSAGE_ID]: message.id,
        };
        break;
      case "genesys-cloud":
      case "genesys-premise":
        metadata = {
          [KEY_METADATA_REPLY_TO_MESSAGE_ID]: message.id,
        };
        bodyType = "standard";
        break;
      // todo: add more chat modes if supported
    }
    try {
      ConversationFactory.instance.sendMessage(reply.text, bodyType, metadata);
      this.dispatchMessageMetadataChange({ id: message.id, metadata });
    } catch (ex) {
      ErrorHandler.captureMessage(["conversation.on.send.content", ex]);
      ErrorHandler.log(
        new HandledError(ex, ErrorCode.CONVERSATION_MESSAGE_SEND_FAILED)
      );
    }
  }

  transformFileMessageToHTML(message: IMessage) {
    return (
      <div class="av-chat-message-file">
        <label>{message.file.name}</label>
        <div class="av-chat-message-file-state">
          {(message.status === "sent" ||
            this.pendingDownloads[message.file.id]) && (
            <av-spinner></av-spinner>
          )}
          {message.status == "ack" &&
            !this.pendingDownloads[message.file.id] && (
              <av-button
                shape="round"
                color="basic"
                size="sm"
                ariaLabel={
                  TranslateService.instant("Download file") +
                  " " +
                  message.file.name
                }
                onClick={() => this.downloadFile(message)}
              >
                <span class="av-chat-message-file-download">↓</span>
              </av-button>
            )}
          {message.status == "error" && <div class="status-error">!</div>}
        </div>
      </div>
    );
  }

  markdownToHTML(text: string) {
    // todo: sanitize
    const md = marked.parse(text) as string;
    return <div innerHTML={md}></div>;
  }

  async downloadFile(message: IMessage) {
    Log.info(`downloading file.. ${message.file.name}`);
    this.pendingDownloads = {
      ...this.pendingDownloads,
      [message.file.id]: true,
    };
    try {
      await ConversationFactory.instance.downloadFile(message.file);
    } catch (ex) {
      NotificationService.error("File Download failed", {
        body: "Please try again",
      });
    } finally {
      this.pendingDownloads = {
        ...this.pendingDownloads,
        [message.file.id]: false,
      };
    }
  }

  /*
  private replaceUrlWithAnchor(
    text: string,
    urlRegex
  ): (string | HTMLElement)[] {
    return text.split(urlRegex).map((part, index) => {
      // If the part matches the regex, return an anchor tag
      if (urlRegex.test(part)) {
        const href = part.startsWith("http") ? part : `http://${part}`; // Add http if missing
        return (
          <a
            href={href}
            target="_blank"
            class="av-chat-message-link"
            rel="noopener noreferrer"
            key={index}
          >
            {part}
          </a>
        );
      }
      // Otherwise, return the normal text
      return part;
    });
  }
  */

  transformTextMessageToHTML(message: IMessage) {
    if (!message.text) {
      return;
    }
    // const regex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)|([\w-]+\.[\w-]+)/g;
    //this.replaceUrlWithAnchor(message.text, regex);
    return this.markdownToHTML(message.text);
  }

  dateToHTML(timestamp: Date) {
    const date = moment(timestamp);
    if (date.isBefore(new Date(), "day")) {
      return date.format("dd Do, HH:mm");
    }
    return date.format("HH:mm");
  }

  inputLoaded(inputRef: HTMLInputElement) {
    if (this.inputRef) {
      return;
    }
    this.inputRef = inputRef;
    this.inputRef.oninput = () => {
      ConversationFactory.instance.sendTyping();
    };

    // doesn't seem to be working. resolving to 'oninput'
    // ConversationFactory.instance.registerTypingInput(inputRef);
    // && ConversationFactory.instance instanceof GenesysChatWidgetService
  }

  // not used for now, we send directly when we attach.
  sendFile() {
    // for some reason (probably security?) if we have opacity:0 to input, we get inupt.files.length == 0.
    // set this.attachments to files if you want to use it (not confirmed to work)
    if (this.fileInputRef.files.length === 0) {
      return;
    }
    try {
      ConversationFactory.instance.sendFile(this.fileInputRef);
    } catch (ex) {
      this.handleFileError(ex);
    }
  }

  private validateFileExtensions(
    files: FileList,
    allowedExtensions: string[]
  ): boolean {
    if (allowedExtensions.length == 0) return true;

    let extensions: string[] = [];
    let mimeTypes: string[] = [];
    const extensionPattern = /\.([0-9a-z]+)(?=[?#])|(\.)(?:[\w]+)$/gim;
    Array.from(files).map((x) =>
      x.name
        .toLowerCase()
        .match(extensionPattern)
        .map((ext) => extensions.push(ext))
    );

    mimeTypes = Array.from(files).map((x) => x.type);

    const allowed = [...extensions, ...mimeTypes].filter((x) =>
      allowedExtensions.includes(x)
    );

    const valid = allowed.length > 0;
    return valid;
  }

  async filesChanged(event: Event) {
    // uncomment to make it into a two-step process, and use (sendFile) above.
    // this.attachments = [(event.target as HTMLInputElement).files.item(0)]

    // Check file size
    const file = (event.target as HTMLInputElement).files.item(0);
    if (this.attachmentConstraints.fileSize) {
      if (file.size >= this.attachmentConstraints.fileSize) {
        NotificationService.error("File size too big", {
          body: `${TranslateService.instant(
            "The maximum allowed file size is"
          )} ${this.attachmentConstraints.friendlyFileSize}`,
        });

        this.fileInputRef.value = "";
        return;
      }
    }

    if (this.attachmentConstraints.mimeType) {
      if (
        !this.validateFileExtensions(
          (event.target as HTMLInputElement).files,
          this.attachmentConstraints.mimeType.split(",")
        )
      ) {
        NotificationService.error("File type not supported");
        this.fileInputRef.value = "";
        return;
      }
    }

    try {
      const response = await ConversationFactory.instance.sendFile(
        event.target as HTMLInputElement
      );
      Log.info(response);
      this.fileInputRef.value = "";
    } catch (ex) {
      this.handleFileError(ex);
    }
  }

  removeAttachment(file: File) {
    this.attachments = this.attachments.filter((f) => f !== file);
  }

  private scrollToBottom() {
    if (this.bodyRef && this.bodyRef.parentElement) {
      CommonService.timer.setTimeout(() => {
        // this.bodyRef.parentElement.scrollTop = this.bodyRef.scrollHeight;
        this.bodyRef.parentElement.scrollTo({
          top: this.bodyRef.parentElement.scrollHeight,
          behavior: "smooth",
        });
      }, 10);
    }
  }

  private handleFileError(ex) {
    this.fileInputRef.value = null;
    let errorMessages = [];
    if (ex.responseText) {
      try {
        const response = JSON.parse(ex.responseText);
        response.errors?.forEach((error) => {
          switch (error.code) {
            case 203:
              errorMessages.push("Unsupported file type.");
              break;
          }
        });
      } catch (ex) {
        AVBusService.errorLog(
          new HandledError(ex, ErrorCode.FILE_TRANSFER_API_ERROR)
        );
        // nothing
      }
    }
    if (errorMessages.length === 0) {
      errorMessages.push("Something went wrong.");
    }
    NotificationService.error("Could not send attachment", {
      body: errorMessages.join(", "),
      errorCode: ErrorCode.FILE_TRANSFER_API_ERROR,
    });
  }

  private confirmCobrowseEnd() {
    NotificationService.confirm(TranslateService.instant("Stop co-browse"), {
      body: TranslateService.instant("Are you sure you want to proceed?"),
      onApprove: () => {
        CobrowseService.terminate("user-terminated");
      },
    });
  }

  get isConversationAudio() {
    return this.$conversationType === ConversationTypeEnum.voiceCall;
  }

  get isDisconnected() {
    return this.$conversationState === ConversationStateEnum.disconnected;
  }

  get isCallStarted() {
    return this.$callState === CallStateEnum.started;
  }

  get canSendFile() {
    return (
      Config.chatAttachmentsEnabled &&
      ((Config.chatMode === "genesys-web-messaging" &&
        Config.webMessagingAttachmentsEnabled) ||
        Config.chatMode === "genesys-premise" ||
        Config.chatMode === "cxone-dfo-chat" ||
        Config.chatMode === "verint-chat")
    );
  }

  get cobrowseStateText() {
    switch (this.$cobrowseState) {
      case CobrowseStateEnum.connecting:
        return TranslateService.instant("Connecting...");
      case CobrowseStateEnum.connected:
        if (this.$cobrowsePermissions.control) {
          return TranslateService.instant("Agent is controlling your page");
        } else if (this.$cobrowsePermissions.view) {
          return TranslateService.instant("Agent is viewing your page");
        } else if (this.$cobrowsePermissions.displayCapture) {
          if (
            this.$displayCaptureDirection ===
            DisplayCaptureDirectionEnum.incoming
          ) {
            return TranslateService.instant("Agent is sharing");
          } else {
            return TranslateService.instant("Sharing screen");
          }
        } else {
          if (this.$cobrowseRequestType === "display-capture") {
            return "";
          } else {
            return TranslateService.instant("Connecting...");
          }
        }
      case CobrowseStateEnum.terminating:
        return TranslateService.instant("Closing...");
    }
  }

  get isCallEnded() {
    return this.$callState === CallStateEnum.ended;
  }
  get isOnCall() {
    return (
      this.$conversationState === ConversationStateEnum.onCall &&
      !this.isCallEnded
    );
  }
  get isOnVideoCall() {
    return (
      this.isOnCall && this.$conversationType === ConversationTypeEnum.videoCall
    );
  }
  get isCallEnding() {
    return this.$callState === CallStateEnum.ending;
  }
  get isConversationAudioCall() {
    return this.$conversationType === ConversationTypeEnum.voiceCall;
  }
  get isLeaving() {
    return this.$conversationState === ConversationStateEnum.leaving;
  }
  get isConversationVideoCall() {
    return this.$conversationType === ConversationTypeEnum.videoCall;
  }
  get isTicketAvailable() {
    return !!this.$ticket;
  }

  get isVideoCardOpen() {
    return (
      this.isOnVideoCall ||
      (this.isCallEnding && !this.isConversationAudioCall) ||
      (this.isLeaving && this.isConversationVideoCall && this.isTicketAvailable)
    );
  }

  get isSharingScreen() {
    return (
      this.$displayCaptureState === DisplayCaptureStateEnum.sharing &&
      this.$displayCaptureDirection === DisplayCaptureDirectionEnum.outgoing
    );
  }

  // get isDisplayCaptureConnectedButNotSharing() {
  //   return (
  //     this.$cobrowseState === CobrowseStateEnum.connected &&
  //     this.$cobrowseRequestType === "display-capture" &&
  //     !!this.$cobrowseRemoteEndpointMeta &&
  //     !this.$displayCaptureState
  //   );
  // }

  get isCobrowseActive() {
    return (
      this.$cobrowseState === CobrowseStateEnum.connected &&
      this.$cobrowsePermissions.view
    );
  }

  render() {
    const isCardHeaderVisible =
      StorageService.isDebugOn ||
      (!this.isConversationAudio &&
        !this.$isPoppedOut &&
        !this.isVideoCardOpen);

    const noAgentText =
      this.$messages.filter((m) => !m.isLocal).length == 0 &&
      (Config.chatMode === "genesys-web-messaging" ||
        Config.queueMessagingEnabled)
        ? "Message us"
        : "Chatting with agent";

    const replyText = TranslateService.instant("write a reply...");
    return (
      <av-card
        role="dialog"
        aria-label={TranslateService.instant("Conversation")}
        open={this.open}
        focused={this.focused}
        class={{
          "av-card-conversation": true,
          "av-card-conversation-secondary":
            this.isConversationAudio || this.$isPoppedOut,
          "av-card-conversation-mobile": DeviceService.isMobile,
          "av-card-conversation-minimized": this.$isMinimized,
        }}
      >
        {isCardHeaderVisible && (
          <av-card-header close-hidden={true}>
            <div class="av-agent-container">
              <av-agent isTray={false} emptyDisplayName={noAgentText}>
                {this.$cobrowseState !== CobrowseStateEnum.idle && (
                  <span>{this.cobrowseStateText}</span>
                )}
                <span slot="agent-avatar">
                  {this.$isMinimized && this.unreadMessagesCount > 0 && (
                    <div
                      aria-label={`${
                        this.unreadMessagesCount
                      } ${TranslateService.instant("unread messages")}`}
                      class="av-notifications"
                    >
                      {this.unreadMessagesCount}
                    </div>
                  )}
                </span>
              </av-agent>
            </div>
            <div
              slot="card-header-actions"
              class="av-card-conversation-header-actions"
            >
              {this.isCobrowseActive && (
                <av-button
                  data-tid="conversation/stop-cobrowse"
                  shape="round"
                  color="danger"
                  onClick={this.confirmCobrowseEnd.bind(this)}
                  size="md"
                  tooltip={TranslateService.instant("Stop co-browse")}
                  tooltipPosition="left"
                >
                  <av-asset image="cobrowse"></av-asset>
                </av-button>
              )}
              {this.isSharingScreen && (
                <app-display-capture
                  size="md"
                  onStopped={this.onDisplayCaptureStopped.bind(this)}
                  tooltipPosition="left"
                ></app-display-capture>
              )}

              <av-button
                ariaLabel={TranslateService.instant(
                  this.$isMinimized
                    ? "Expand conversation"
                    : "Collapse conversation"
                )}
                data-tid="conversation/expand-collapse"
                class="card-header-action"
                shape="round"
                size="md"
                color="basic"
                onClick={() => this.toggleMinimize()}
              >
                {this.$isMinimized && <av-asset image="chevron-up"></av-asset>}
                {!this.$isMinimized && (
                  <av-asset image="chevron-down"></av-asset>
                )}
              </av-button>
            </div>
          </av-card-header>
        )}

        <div slot="card-body" ref={this.registerBodyRef.bind(this)}>
          {this.$messages
            .sort((m1, m2) =>
              !!m1.index && !!m2.index
                ? m1.index - m2.index
                : m1.timestamp < m2.timestamp
                ? -1
                : 1
            )
            .map((message) => (
              <div
                role={message.isLocal ? "group" : "alert"}
                class={{
                  "av-chat-row": true,
                  "av-chat-row-local": message.isLocal,
                  "av-chat-row-notice": message.type === "notice",
                }}
              >
                {this.transformToHTML(message)}
              </div>
            ))}
          {this.$isAgentTyping && (
            <div class="av-chat-row">
              <div class="av-chat-message ag-chat-message-typing">
                <div class="av-typing-loader">
                  <av-translate>typing</av-translate>...
                </div>
              </div>
              <div class="av-chat-message-hint">
                <av-translate>user is typing</av-translate>
              </div>
            </div>
          )}
          {this.isDisconnected && (
            <div class="av-chat-row av-chat-row-error">
              <av-translate>Connection lost</av-translate>
            </div>
          )}
        </div>

        <div class="av-card-footer" slot="card-footer">
          {this.attachments.length > 0 && (
            <div class="av-attachment-container">
              <div class="av-attachment-list">
                {this.attachments.map((a) => (
                  <div class="av-attachment">
                    <div class="av-attachment-name">{a.name}</div>
                    <av-button
                      data-tid="conversation/remove-attachment"
                      onClick={() => this.removeAttachment(a)}
                      shape="round"
                      size="xs"
                      color="primary"
                    >
                      <av-asset color="light" image="close"></av-asset>
                    </av-button>
                  </div>
                ))}
              </div>
              <div class="av-attachment-action">
                <av-button
                  data-tid="conversation/send-attachment"
                  onClick={this.sendFile.bind(this)}
                  size="md"
                  color="link"
                >
                  <av-translate>Send</av-translate>
                </av-button>
              </div>
            </div>
          )}
          <div class="av-card-editor">
            <form onSubmit={this.send.bind(this)} autocomplete="off">
              <input
                data-tid="conversation/text-message"
                disabled={this.isDisconnected}
                ref={(el) => this.inputLoaded(el)}
                name="message"
                type="text"
                onInput={this.handleChange.bind(this)}
                placeholder={replyText}
              />
              <div class="av-card-editor-tools">
                {this.canSendFile && (
                  <div
                    role="button"
                    aria-label={TranslateService.instant("Attach file")}
                    class="av-card-editor-tool av-card-editor-tool-attach"
                  >
                    <input
                      data-tid="conversation/attach-file"
                      type="file"
                      accept={this.attachmentConstraints?.mimeType}
                      onChange={this.filesChanged.bind(this)}
                      ref={(el) => (this.fileInputRef = el)}
                    />
                    <av-asset color="theme" image="attach"></av-asset>
                  </div>
                )}
                <button
                  type="submit"
                  aria-label={TranslateService.instant("Send message")}
                  class="av-card-editor-tool"
                >
                  <av-asset color="theme" image="send"></av-asset>
                </button>
              </div>
            </form>
          </div>
        </div>
      </av-card>
    );
  }
}
